/**
 * Event Category Landing Page - Public landing page for event categories
 * 
 * Displays rich content for event categories like "Decorating Classes" or "Birthday Parties"
 * URL: /events/:slug (e.g., /events/decorating-classes)
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { applySeo } from '../utils/seo';
import { BreadcrumbSchema } from '../components/StructuredData';
import { DEFAULT_OG_IMAGE } from '../utils/seoDefaults';
import { publicLoadErrorMessage } from '../lib/publicSiteErrorMessage';

// API URL
const API_URL = import.meta.env.VITE_API_URL || '';

interface LandingPageContent {
  hero?: {
    title?: string;
    description?: string;
    image_url?: string;
  };
  sections?: Array<{
    type: string;
    title?: string;
    content?: string;
    items?: Array<{
      title: string;
      description: string;
      icon?: string;
    }>;
  }>;
  pricing?: {
    starting_at?: number;
    price_text?: string;
    pricing_details?: string;
  };
  cta?: {
    title?: string;
    description?: string;
    button_text?: string;
    button_link?: string;
  };
  seo?: {
    meta_title?: string;
    meta_description?: string;
    meta_keywords?: string;
    og_image_url?: string;
  };
  gallery_images?: string[];
}

interface EventCategory {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  image_url: string | null;
  color: string;
  has_landing_page: boolean;
  landing_page_content: LandingPageContent | null;
}

export default function EventCategoryLandingPage() {
  const { slug } = useParams<{ slug: string }>();
  const [category, setCategory] = useState<EventCategory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contactInfo, setContactInfo] = useState({
    phone: '(705) 503-4966',
    email: 'nannybbakedgoods@outlook.com',
  });

  useEffect(() => {
    const fetchCategory = async () => {
      if (!slug) return;

      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_URL}/api/event-categories/slug/${slug}`);

        if (!response.ok) {
          if (response.status === 404) {
            setError('Event category not found');
          } else {
            throw new Error('Failed to load event category');
          }
          return;
        }

        const data = await response.json();
        const categoryData = data.category;

        if (!categoryData.has_landing_page) {
          setError('This event category does not have a landing page');
          return;
        }

        setCategory(categoryData);
      } catch (err) {
        console.error('Event category landing load:', err);
        setError(publicLoadErrorMessage());
      } finally {
        setLoading(false);
      }
    };

    fetchCategory();

    // Fetch contact info
    fetch('/api/settings/contact')
      .then((res) => res.json())
      .then((data) => {
        if (data.phone || data.email) {
          setContactInfo({
            phone: data.phone || contactInfo.phone,
            email: data.email || contactInfo.email,
          });
        }
      })
      .catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (!category) return;
    const seo = category.landing_page_content?.seo;
    const description =
      seo?.meta_description ||
      category.description ||
      `Join ${category.name} at Nanny B's Bakery in Barrie, Ontario. View schedules, reserve your spot, and learn hands-on cake decorating skills.`;
    applySeo({
      title: seo?.meta_title || `${category.name} | Nanny B's Bakery`,
      description,
      keywords: seo?.meta_keywords || undefined,
      canonicalPath: `/events/${category.slug}`,
      og: {
        type: 'website',
        title: seo?.meta_title || `${category.name} | Nanny B's Bakery`,
        description,
        image: seo?.og_image_url || category.image_url || DEFAULT_OG_IMAGE,
      },
    });
  }, [category]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-bakery-cream to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !category) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-bakery-cream to-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-4">🎂</div>
          <h1 className="text-2xl font-display font-bold text-gray-900 mb-2">
            {error === 'Event category not found' ? 'Page Not Found' : 'Oops!'}
          </h1>
          <p className="text-gray-600 mb-6">
            {error || 'Something went wrong. Please try again.'}
          </p>
          <Link to="/" className="btn-primary">
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  const content = category.landing_page_content || {};
  const hero = content.hero || {};
  const heroTitle = hero.title || category.name;
  const heroDescription = hero.description || category.description || '';
  const heroImage = hero.image_url || category.image_url;

  return (
    <div className="bg-gradient-to-b from-bakery-cream to-white min-h-screen">
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: 'https://nannybbakery.ca/' },
          { name: 'Events', url: 'https://nannybbakery.ca/events' },
          { name: category.name, url: `https://nannybbakery.ca/events/${category.slug}` },
        ]}
      />
      {/* Hero Section */}
      <section className="relative">
        {heroImage ? (
          <div className="h-80 md:h-[500px] relative">
            <img
              src={heroImage}
              alt={heroTitle}
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 text-white">
              <div className="container-custom">
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-4xl md:text-6xl font-display font-bold mb-4"
                >
                  {heroTitle}
                </motion.h1>
                {heroDescription && (
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-xl text-gray-200 max-w-3xl"
                  >
                    {heroDescription}
                  </motion.p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="gradient-primary text-white py-16 md:py-24">
            <div className="container-custom text-center">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-4xl md:text-6xl font-display font-bold mb-6"
              >
                {heroTitle}
              </motion.h1>
              {heroDescription && (
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="text-xl text-gray-100 max-w-3xl mx-auto"
                >
                  {heroDescription}
                </motion.p>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Back Link */}
      <div className="container-custom py-4">
        <Link
          to="/about"
          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to About
        </Link>
      </div>

      {/* Content Sections */}
      {content.sections && content.sections.length > 0 && (
        <section className="section-padding pt-0">
          <div className="container-custom space-y-16">
            {content.sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={index % 2 === 0 ? 'bg-white' : 'bg-bakery-cream'}
              >
                <div className="container-custom py-12">
                  {section.title && (
                    <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-6">
                      {section.title}
                    </h2>
                  )}

                  {section.type === 'story' && section.content && (
                    <div className="prose prose-lg max-w-none">
                      <p className="text-gray-700 text-lg leading-relaxed whitespace-pre-line">
                        {section.content}
                      </p>
                    </div>
                  )}

                  {section.type === 'features' && section.items && (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {section.items.map((item, itemIndex) => (
                        <div
                          key={itemIndex}
                          className="bg-white p-6 rounded-xl shadow-md"
                        >
                          {item.icon && (
                            <div className="text-4xl mb-4">{item.icon}</div>
                          )}
                          <h3 className="text-xl font-display font-semibold text-gray-900 mb-2">
                            {item.title}
                          </h3>
                          <p className="text-gray-600">{item.description}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Pricing Section */}
      {content.pricing && (
        <section className="section-padding bg-white">
          <div className="container-custom text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
                Pricing
              </h2>
              {content.pricing.starting_at && (
                <p className="text-4xl font-bold text-primary-600 mb-2">
                  ${content.pricing.starting_at.toFixed(2)}
                </p>
              )}
              {content.pricing.price_text && (
                <p className="text-xl text-gray-600 mb-4">
                  {content.pricing.price_text}
                </p>
              )}
              {content.pricing.pricing_details && (
                <p className="text-gray-600 max-w-2xl mx-auto">
                  {content.pricing.pricing_details}
                </p>
              )}
            </motion.div>
          </div>
        </section>
      )}

      {/* Gallery Section */}
      {content.gallery_images && content.gallery_images.length > 0 && (
        <section className="section-padding bg-bakery-cream">
          <div className="container-custom">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-8 text-center">
              Gallery
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {content.gallery_images.map((imageUrl, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="aspect-square rounded-xl overflow-hidden shadow-lg group"
                >
                  <img
                    src={imageUrl}
                    alt={`Gallery image ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="section-padding bg-primary-50">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
              {content.cta?.title || 'Ready to Get Started?'}
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              {content.cta?.description || 'Contact us to learn more or book your event!'}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href={`tel:${contactInfo.phone.replace(/\D/g, '')}`}
                className="btn-primary inline-flex items-center gap-2"
              >
                <PhoneIcon className="w-5 h-5" />
                Call {contactInfo.phone}
              </a>
              <a
                href={`mailto:${contactInfo.email}`}
                className="btn-outline inline-flex items-center gap-2"
              >
                <EnvelopeIcon className="w-5 h-5" />
                Email Us
              </a>
              {content.cta?.button_link && (
                <Link
                  to={content.cta.button_link}
                  className="btn-outline"
                >
                  {content.cta.button_text || 'Learn More'}
                </Link>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
